/**
 * This is a utility to override the Application Insights endpoint URL
 * to use our proxy instead of direct connection to Azure
 */

import { getServerUrl } from '../config/runtimeConfig';

// Call this function after initializing Application Insights to patch its endpoint URL
export function patchAppInsightsEndpoint(appInsights) {
  if (!appInsights || !appInsights.core) {
    console.warn('Cannot patch App Insights endpoint - instance not initialized');
    return false;
  }

  try {
    // In development, we might not need the proxy - check if we should skip
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Development mode detected - skipping App Insights proxy patching');
      return true; // Return true to indicate "success" but no actual patching needed
    }
    
    // Get the channel from the core directly
    const channel = appInsights.core?.channel;
    
    if (!channel) {
      // Instead of showing a warning, try to set up a delayed retry
      console.log('📡 App Insights channel not ready yet, will retry in background...');
      
      // Set up a background retry mechanism
      setTimeout(() => {
        const retryChannel = appInsights.core?.channel;
        if (retryChannel) {
          console.log('✅ App Insights channel found on retry, applying proxy patch...');
          return attemptProxyPatch(appInsights, retryChannel);
        } else {
          console.log('ℹ️ App Insights channel still not available after retry - continuing without proxy');
        }
      }, 1000); // Retry after 1 second
      
      return true; // Don't fail initialization
    }
    
    return attemptProxyPatch(appInsights, channel);
  } catch (error) {
    console.error('Failed to patch App Insights endpoint URL:', error);
    return false;
  }
}

// Helper function to attempt the actual proxy patching
function attemptProxyPatch(appInsights, channel) {
  try {
    // Get the sender object from the channel
    const sender = channel._sender;
    if (!sender) {
      console.warn('No sender found in App Insights channel');
      return false;
    }
    
    // Add the API URL, ensuring it has the correct format
    let apiBaseUrl = getServerUrl();
    
    // Trim trailing slash if present
    if (apiBaseUrl.endsWith('/')) {
      apiBaseUrl = apiBaseUrl.slice(0, -1);
    }
    
    // Make sure we have a valid URL
    if (!apiBaseUrl) {
      console.warn('No backend URL configured, cannot use App Insights proxy');
      return false;
    }
    
    const fullProxyUrl = `${apiBaseUrl}/app-insights`;
    
    console.log(`Patching App Insights endpoint URL to use proxy: ${fullProxyUrl}`);
    
    // Override the endpoint URL with more robust error handling
    try {
      sender._endpoint = fullProxyUrl;
    } catch (endpointError) {
      console.error('Error setting sender endpoint:', endpointError);
      return false;
    }
    
    // If there's a _beaconSender, patch that too
    if (sender._beaconSender) {
      sender._beaconSender._endpoint = fullProxyUrl;
    }
    
    // If there's an xhr sender, patch that too
    if (sender._xhrSender) {
      sender._xhrSender._endpoint = fullProxyUrl;
    }
    
    console.log('Successfully patched App Insights endpoint URL');
    return true;
  } catch (error) {
    console.error('Failed to patch App Insights endpoint URL:', error);
    return false;
  }
}
